import React, { useCallback, useEffect, useState } from 'react'
import { getNotificationHost } from '../../../../modules/apiHost'

const downloadLinkClassName =
  'tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-lg tw-text-xs tw-font-medium tw-border tw-no-underline'

function CopyButton({ text, label = 'Copy example JSON' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }, [text])

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-lg tw-text-xs tw-font-medium tw-border tw-transition-colors ${
        copied
          ? 'tw-bg-emerald-50 tw-text-emerald-700 tw-border-emerald-300'
          : 'tw-bg-white tw-text-slate-700 tw-border-slate-300 hover:tw-bg-slate-50'
      }`}
    >
      {copied ? (
        <>
          <i className="fa fa-check" />
          Copied
        </>
      ) : (
        <>
          <i className="fa fa-copy" />
          {label}
        </>
      )}
    </button>
  )
}

type ApiStoreSpecExportsProps = {
  exampleJson: string
  onExampleLoaded?: (formattedExample: string) => void
}

export default function ApiStoreSpecExports({ exampleJson, onExampleLoaded }: ApiStoreSpecExportsProps) {
  const notificationHost = getNotificationHost(window.location.hostname)
  const openApiUrl = `${notificationHost}/api/v1/orders/openapi.json`
  const legacySchemaUrl = `${notificationHost}/api/order/schema.json`
  const exampleUrl = `${notificationHost}/api/v1/orders/example.json`

  useEffect(() => {
    let cancelled = false

    fetch(exampleUrl)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data || !onExampleLoaded) return
        onExampleLoaded(JSON.stringify(data, null, 2))
      })
      .catch(() => {
        // Keep inline fallback example when notification server is unreachable.
      })

    return () => {
      cancelled = true
    }
  }, [exampleUrl, onExampleLoaded])

  return (
    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-mb-3">
      <a
        href={openApiUrl}
        download="api-store-orders-v1.openapi.json"
        target="_blank"
        rel="noopener noreferrer"
        className={`${downloadLinkClassName} tw-border-indigo-200 tw-bg-indigo-50 tw-text-indigo-900 hover:tw-bg-indigo-100`}
      >
        <i className="fa fa-download" />
        Download OpenAPI (v1)
      </a>
      <a
        href={legacySchemaUrl}
        download="api-store-order.schema.json"
        target="_blank"
        rel="noopener noreferrer"
        className={`${downloadLinkClassName} tw-border-violet-200 tw-bg-violet-50 tw-text-violet-900 hover:tw-bg-violet-100`}
      >
        <i className="fa fa-download" />
        Download legacy JSON Schema
      </a>
      <CopyButton text={exampleJson} />
    </div>
  )
}
